import { createFileRoute, Link, Outlet, useMatches, useNavigate } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { Search, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { KpiCard } from '@/components/kpi-card';
import { DateRangePicker } from '@/components/date-range-picker';
import { useStore } from '@/lib/store-context';
import { useSalesFilters } from '@/lib/sales-filters';
import { getPurchaseBills, getPurchaseSummary } from '@/lib/purchases-api';
import { fmtINR, fmtNumber, fmtDate } from '@/lib/format';
import * as React from 'react';

const Search$ = z.object({
  q: z.string().optional(),
});

export const Route = createFileRoute('/_app/purchases')({
  validateSearch: Search$,
  component: Page,
});

function Page() {
  // When /purchases/bill/... is active, defer to the child route.
  const matches = useMatches();
  const onChild = matches.some((m) => m.routeId.startsWith('/_app/purchases/'));
  if (onChild) return <Outlet />;
  return <BillsList />;
}

const PAGE_SIZE = 50;

function BillsList() {
  const { from, to, store, page, setFilters } = useSalesFilters();
  const { selected: _selectedStore } = useStore();
  void _selectedStore;
  const search = Route.useSearch();
  const nav = Route.useNavigate();
  const router = useNavigate();
  const q = search.q ?? '';

  const [draft, setDraft] = React.useState(q);
  React.useEffect(() => setDraft(q), [q]);
  React.useEffect(() => {
    const t = setTimeout(() => {
      const next = draft.trim();
      if (next !== q) nav({ to: '.', search: (prev) => ({ ...prev, q: next || undefined }), replace: true });
    }, 250);
    return () => clearTimeout(t);
  }, [draft, q, nav]);

  const summaryQ = useQuery({
    queryKey: ['purchases:summary', from, to, store],
    queryFn: () => getPurchaseSummary({ from, to, store }),
    staleTime: 60_000,
  });
  const billsQ = useQuery({
    queryKey: ['purchases:bills', from, to, store, page, q],
    queryFn: () => getPurchaseBills({ from, to, store, page, limit: PAGE_SIZE, q: q || null }),
    placeholderData: keepPreviousData,
  });

  const totalPages = billsQ.data ? Math.max(1, Math.ceil(billsQ.data.total / PAGE_SIZE)) : 1;
  const totals = summaryQ.data;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Daleep · Purchases</p>
          <h1 className="font-display text-3xl font-medium tracking-tight text-ink sm:text-4xl">Purchase Bills</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            INV_PURCHASE2 · TRANTYPE = 'PR' · vendor recorded as REFBILL_NO (free text)
          </p>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link to="/purchases/returns" search={(prev) => prev}>Returns →</Link>
        </Button>
      </header>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard label="Purchases · value" value={totals ? fmtINR(totals.purchases.value) : '—'} hint={totals ? `${fmtNumber(totals.purchases.count)} bills` : ''} />
        <KpiCard label="Returns · value"   value={totals ? fmtINR(totals.returns.value)   : '—'} hint={totals ? `${fmtNumber(totals.returns.count)} bills`   : ''} />
        <KpiCard label="Net spend"          value={totals ? fmtINR(totals.purchases.value - totals.returns.value) : '—'} hint="Purchases − returns" />
        <KpiCard label="Days with activity" value={totals ? fmtNumber(totals.daily.length) : '—'} />
      </div>

      {/* Filter bar — top-left, consistent with /sales/bills and other pages. */}
      <div className="flex flex-wrap items-center gap-3">
        <DateRangePicker from={from} to={to} onChange={(r) => setFilters({ from: r.from, to: r.to, page: 1 })} />
        <div className="relative w-full min-w-[260px] sm:w-72">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Bill #, ref bill, entered-by…"
            className="h-10 pl-8 pr-8"
          />
          {draft && (
            <button type="button" onClick={() => setDraft('')} aria-label="Clear"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-ink">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            Bills {billsQ.data && <span className="text-sm font-normal text-muted-foreground">({fmtNumber(billsQ.data.total)} total)</span>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {billsQ.isLoading && <p>Loading…</p>}
          {billsQ.error && <p className="text-sm text-destructive">{(billsQ.error as Error).message}</p>}
          {billsQ.data && (
            <>
              <Table>
                <THead>
                  <TR>
                    <TH>Bill #</TH>
                    <TH>Date</TH>
                    <TH>Store</TH>
                    <TH>Vendor bill (ref)</TH>
                    <TH className="text-right">Net</TH>
                    <TH className="text-right">Total</TH>
                    <TH>Tender</TH>
                    <TH>Entered by</TH>
                  </TR>
                </THead>
                <TBody>
                  {billsQ.data.bills.map((b) => {
                    const onOpen = () => router({
                      to: '/purchases/bill/$storeCode/$billDate/$billNo',
                      params: { storeCode: b.storeCode, billDate: b.billDate, billNo: b.billNo },
                    });
                    return (
                      <TR
                        key={`${b.billDate}-${b.storeCode}-${b.billNo}`}
                        className="cursor-pointer"
                        tabIndex={0}
                        role="button"
                        aria-label={`Open purchase ${b.billNo}`}
                        onClick={onOpen}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
                      >
                        <TD className="font-mono">{b.billNo}</TD>
                        <TD>{fmtDate(b.billDate)}</TD>
                        <TD>{b.storeCode}</TD>
                        <TD className="font-mono text-xs text-muted-foreground">{b.refBillNo || '—'}</TD>
                        <TD className="text-right">{fmtINR(b.netAmt)}</TD>
                        <TD className="text-right font-medium">{fmtINR(b.billAmt)}</TD>
                        <TD>{b.tender}</TD>
                        <TD className="text-xs text-muted-foreground">{b.enteredBy}</TD>
                      </TR>
                    );
                  })}
                  {billsQ.data.bills.length === 0 && (
                    <TR><TD colSpan={8} className="py-6 text-center text-sm text-muted-foreground">No purchase bills in this range.</TD></TR>
                  )}
                </TBody>
              </Table>
              <div className="mt-3 flex items-center justify-end gap-2 text-sm">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setFilters({ page: page - 1 })}>Prev</Button>
                <span className="px-2">Page {page} / {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setFilters({ page: page + 1 })}>Next</Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
